/**
 * Pure parsing of the Global Bar's `@opti-common-header:change` event
 * detail into a concrete navigation target.
 *
 * The Global Bar normally navigates the user itself when an instance is
 * picked from the product switcher (via the instance's `NavigationUrl`).
 * When we run it with `disable-navigation="true"` — which we only do while
 * the session is acting as a VAU — that auto-navigation is suppressed and
 * we become responsible for performing it. This helper turns the emitted
 * detail into the URL we should send the user to, so the VAU guard can ask
 * for confirmation and then release + navigate.
 *
 * Event detail shape (per the Global Bar docs / home-app consumer):
 *   {
 *     organization: { text, value, type } | null,
 *     product: {
 *       text, href, value,
 *       valueObject: { ProductName, NavigationUrl, ... }
 *     } | null,
 *     instance: {
 *       text, href, value,
 *       valueObject: { InstanceId, InstanceName, NavigationUrl, ... },
 *       type
 *     } | null
 *   }
 */

export type GlobalBarChangeInstance = {
  text?: string;
  href?: string | null;
  value?: string;
  valueObject?: {
    InstanceName?: string;
    ProductName?: string;
    NavigationUrl?: string;
    [key: string]: unknown;
  };
};

export type GlobalBarChangeProduct = {
  text?: string;
  href?: string | null;
  value?: string;
  valueObject?: {
    ProductName?: string;
    NavigationUrl?: string;
    [key: string]: unknown;
  };
};

export type GlobalBarChangeDetail = {
  organization?: { text?: string; value?: string } | null;
  product?: GlobalBarChangeProduct | null;
  instance?: GlobalBarChangeInstance | null;
};

export type GlobalBarSwitchTarget = {
  /** Where to send the user once they confirm the switch. */
  url: string;
  /** Human label for the destination, used in the confirmation copy. */
  label: string;
};

/**
 * Defence-in-depth allowlist for a URL we're about to hand to
 * `window.location.assign`. The value originates in the global bar's change
 * event `detail`, so we constrain it to either a same-origin in-app route or
 * an absolute `https://` URL — never `javascript:`, `data:`, a
 * protocol-relative `//host`, or plain `http:`. Optimizely-controlled
 * usermgmt data shouldn't produce anything unsafe today, but this keeps an
 * untrusted-looking value from flowing straight into `assign()`.
 */
export function isSafeSwitchUrl(url: string): boolean {
  // Same-origin in-app route, but reject protocol-relative "//host".
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    // Not an absolute URL (and not a recognised in-app route) → not safe.
    return false;
  }
}

/**
 * Append an Okta `login_hint` so that, if the destination product bounces
 * the user through a login screen (e.g. their product session has expired),
 * it pre-fills the principal — not the VAU. Only applied to external
 * NavigationUrls; in-app Opal routes read identity from the session that the
 * release already swapped back, so no hint is needed there.
 *
 * Note: this only guarantees we *emit* the hint. Whether a given product
 * forwards it into its Okta authorize call is that product's concern; the
 * hint is inert (harmless) when it isn't honoured.
 */
function appendLoginHint(url: string, email?: string): string {
  if (!email) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}login_hint=${encodeURIComponent(email)}`;
}

/**
 * Resolve the navigation target for a global-bar selection, or `null` when
 * the change does not represent a navigation away from the current instance
 * (org-only selection, re-selecting the current instance, or a
 * product-only selection that carries no navigable URL).
 *
 * A product-only selection (Home / App Directory — `instance: null,
 * product: {...}`) resolves from the product entry's URL when present, so it
 * doesn't dead-end while the bar's own navigation is suppressed. When such a
 * selection has no usable URL the caller is expected to surface a hint
 * rather than silently drop it (see `useVauSwitchGuard`).
 */
export function resolveGlobalBarSwitchTarget(
  detail: GlobalBarChangeDetail | undefined | null,
  opts: { currentInstanceId?: string | null; principalEmail?: string }
): GlobalBarSwitchTarget | null {
  const instance = detail?.instance;
  const instanceId = instance?.value;

  if (instance && instanceId) {
    // Re-selecting the instance we're already on is a no-op.
    if (opts.currentInstanceId && instanceId === opts.currentInstanceId) return null;

    const label =
      instance.text ||
      instance.valueObject?.InstanceName ||
      instance.valueObject?.ProductName ||
      'the selected workspace';

    const navigationUrl = (instance.valueObject?.NavigationUrl ?? instance.href ?? '').trim();

    if (navigationUrl && isSafeSwitchUrl(navigationUrl)) {
      return { url: appendLoginHint(navigationUrl, opts.principalEmail), label };
    }

    // No usable NavigationUrl (localdev / another Opal instance) or an
    // unsafe scheme we refuse to hand to assign() — fall back to the in-app
    // chat route for the selected instance.
    return { url: `/instances/${instanceId}/chat`, label };
  }

  // No instance. A product-only selection (Home / App Directory) still wants
  // to navigate; resolve it from the product entry when it carries a URL so
  // the guard can confirm + release + navigate instead of dead-ending.
  const product = detail?.product;
  if (product) {
    const productUrl = (product.valueObject?.NavigationUrl ?? product.href ?? '').trim();
    if (productUrl && isSafeSwitchUrl(productUrl)) {
      const label = product.text || product.valueObject?.ProductName || 'the selected product';
      return { url: appendLoginHint(productUrl, opts.principalEmail), label };
    }
  }

  // Org-only change, or a product selection with no navigable URL — nothing
  // to navigate to here. (The guard surfaces a "not available while acting
  // as a VAU" hint for the product-with-no-URL case so it isn't a silent
  // dead-end.)
  return null;
}
